import * as React from 'react';

import styled from 'styled-components';

import { COLOR, FONT_SIZE, SPEED, Theme } from '../../../../packages/constants';

interface ChartIconProps {
    iconName: string;
    iconLabel: string;
    id: string;
    onChange: (e: any) => void;
    isSelected: boolean;
    assistAccess?: boolean;
    dataTestId?: string;
    imagePath?: string;
    onKeyDown?: (e: any) => void;
    hasModernStyles?: boolean;
}

interface StyledIconProps {
    iconName: string;
    imagePath?: string;
    assistAccess?: boolean;
}

interface StyledInputProps {
    assistAccess?: boolean;
    ref?: any;
    rel?: string;
    autoComplete?: string;
    dataTestId?: string;
    hasModernStyles?: boolean;
    theme?: Theme;
}

interface DataTestIdProps {
    dataTestId: string;
}

const ChartIconWrapper = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 100px;
    width: 33%;
    flex-basis: 33%;
`;

const StyledLabel = styled.label`
    width: 100px;
    height: 100px;
    display: block;
    position: relative;
    cursor: pointer;
`;

export const StyledIcon = styled.div<StyledIconProps>`
    box-sizing: border-box;
    margin-bottom: 12px;
    border: 3px solid ${COLOR.MAIN.ICON_BACKGROUND};
    border-radius: 15px;
    width: 100px;
    height: 100px;
    background-image: url(${(props) => `${props.imagePath ?? ''}${props.iconName}.svg`});
    background-color: ${COLOR.MAIN.ICON_BACKGROUND};
    background-repeat: no-repeat;
    background-position: center;
    background-size: 120% auto;
    box-shadow: 2px 2px 4px rgba(4, 37, 91, 0.15);
    transition: box-shadow ${SPEED.TRANSITION};
`;

const StyledLabelText = styled.div`
    color: black;
    border-radius: 3px;
    font-size: ${FONT_SIZE.SMALL};
    font-family: sans-serif;
    line-height: ${FONT_SIZE.LARGE};
    padding: 5px 2px;
    text-align: center;
    background-color: ${COLOR.PRIMARY.WHITE};
    box-shadow: 0 0 15px rgba(4, 37, 91, 0);
`;

const Radio = styled.input.attrs<DataTestIdProps>(({ dataTestId }) => ({
    'data-test-id': dataTestId,
}))``;

const StyledRadio = styled(Radio)<StyledInputProps>`
    position: absolute;
    opacity: 0;
    top: 0;
    left: 0;
    z-index: -1;

    &:hover {
        ~ ${StyledIcon} {
            box-shadow: 0 0 15px rgba(4, 37, 91, 0.3);
        }
    }

    &:focus ~ ${StyledIcon} {
        outline: ${(props) => (props.assistAccess ? '1px dotted #212121' : '0')};
        outline: ${(props) => (props.assistAccess ? '5px auto -webkit-focus-ring-color' : '0')};
    }

    &:checked {
        ~ ${StyledIcon} {
            border-color: ${(props: StyledInputProps) =>
                props.hasModernStyles && props.theme?.mainBorder ? props.theme.mainBorder : COLOR.BRAND.REPORTING};
            box-shadow: 0 0 15px rgba(4, 37, 91, 0.3);
        }

        ${(props) =>
            !props.hasModernStyles &&
            `
            ~ ${StyledLabelText} {
                color: white;
                background-color: ${COLOR.BRAND.REPORTING};
                box-shadow: 0 0 15px rgba(4, 37, 91, 0.3);
            }
        `}
    }
`;

export const StyledChartWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    overflow: auto;
    padding: 15px 0 0 0;
    height: calc(100% - 95px);
    position: relative;
`;

const ChartIcon = React.forwardRef<HTMLInputElement, ChartIconProps>(
    (
        {
            iconName,
            iconLabel,
            id,
            onChange,
            onKeyDown,
            assistAccess,
            isSelected,
            dataTestId,
            imagePath,
            hasModernStyles,
        },
        ref,
    ) => (
        <ChartIconWrapper data-test-id={dataTestId}>
            <StyledLabel data-test-id={`${dataTestId}__label`}>
                <StyledRadio
                    dataTestId={`${dataTestId}__radio`}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    type={'radio'}
                    name={'chart-icon'}
                    value={id}
                    ref={ref}
                    assistAccess={assistAccess}
                    defaultChecked={isSelected}
                    hasModernStyles={hasModernStyles}
                />
                <StyledIcon assistAccess={assistAccess} iconName={iconName} imagePath={imagePath} />
                <StyledLabelText>{iconLabel}</StyledLabelText>
            </StyledLabel>
        </ChartIconWrapper>
    ),
);
ChartIcon.displayName = 'ChartIcon';

export default ChartIcon;
